/**
 * Platform auth provider — phone-first customer sessions.
 * Registration IS the first successful OTP; no separate signup form.
 */
import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from "react";
import { platformApi, tokenStore, type PlatformUser } from "./api";

interface PlatformAuthValue {
  user: PlatformUser | null;
  isLoading: boolean;
  requestOtp: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, code: string) => Promise<{ isNewUser: boolean }>;
  refreshUser: () => Promise<void>;
  signOut: () => Promise<void>;
}

const PlatformAuthContext = createContext<PlatformAuthValue | null>(null);

export function usePlatformAuth() {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error("usePlatformAuth must be used within PlatformAuthProvider");
  return ctx;
}

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PlatformUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (!tokenStore.hasSession) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    try {
      setUser(await platformApi.me());
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const requestOtp = useCallback(async (phone: string) => {
    await platformApi.requestOtp(phone);
  }, []);

  const verifyOtp = useCallback(async (phone: string, code: string) => {
    const data = await platformApi.verifyOtp(phone, code);
    await refreshUser();
    return { isNewUser: data.is_new_user };
  }, [refreshUser]);

  const signOut = useCallback(async () => {
    await platformApi.logout();
    setUser(null);
  }, []);

  return (
    <PlatformAuthContext.Provider value={{ user, isLoading, requestOtp, verifyOtp, refreshUser, signOut }}>
      {children}
    </PlatformAuthContext.Provider>
  );
}

/** Route guard for customer surfaces on platform auth. */
export function PlatformProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = usePlatformAuth();
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!user) {
    window.location.replace("/login");
    return null;
  }
  return <>{children}</>;
}
